import useTheme from "@/contexts/theme/theme.context-hook";
import { ButtonAnswer, ButtonDecline, ButtonHangUp, ButtonHold, ButtonResume, CallInputs, CallOutputs, Keypad } from "@EspaceParkQC/sdk-call-center";
import { Button, cn, Drawer, DrawerContent, DrawerTrigger, Tooltip, TooltipContent, TooltipTrigger } from "@EspaceParkQC/sdk-react";
import { IconDialpadFilled } from "@tabler/icons-react";
import { Mic, MicOff, Pause, Phone, Play, Volume2, VolumeX } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Fragment } from "react/jsx-runtime";

function PopoverPhonepadWithTooltip({
  onKeyPress,
  isAvailable,
  tooltip,
}: {
  onKeyPress: (digit: string) => void | Promise<void>;
  isAvailable: boolean;
  tooltip: string;
}): React.ReactElement {
  const [visible, setVisible] = useState(false);
  const theme = useTheme();
  const isDark = theme.resolvedTheme === "dark";

  return (
    <Drawer open={visible} onOpenChange={(open) => setVisible(open)}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DrawerTrigger asChild>
            <Button
              variant="outline"
              className={cn("md:size-[64px] size-[42px] rounded-full", !isDark && "border-slate-300")}
              disabled={!isAvailable}
            >
              <IconDialpadFilled className="size-6" fill="currentColor" stroke="transparent" />
            </Button>
          </DrawerTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
      <DrawerContent>
        <div className="flex flex-col items-center gap-4 p-6">
          <div className="grid grid-cols-3 gap-3 w-full max-w-xs">
            {[
              { digit: "1", letters: "" },
              { digit: "2", letters: "ABC" },
              { digit: "3", letters: "DEF" },
              { digit: "4", letters: "GHI" },
              { digit: "5", letters: "JKL" },
              { digit: "6", letters: "MNO" },
              { digit: "7", letters: "PQRS" },
              { digit: "8", letters: "TUV" },
              { digit: "9", letters: "WXYZ" },
              { digit: "*", letters: "" },
              { digit: "0", letters: "+" },
              { digit: "#", letters: "" },
            ].map(({ digit, letters }) => (
              <Button
                key={digit}
                onClick={(e) => {
                  e.preventDefault();
                  onKeyPress(digit);
                }}
                disabled={!isAvailable}
                variant="outline"
                size="lg"
              >
                <span>{digit}</span>
                {letters && <span className="text-xs font-normal text-muted-foreground">{letters}</span>}
              </Button>
            ))}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export default function CallControls(): React.ReactElement {
  const theme = useTheme();
  const isDark = theme.resolvedTheme === "dark";
  const { t } = useTranslation("main");

  return (
    <div
      className={cn(
        "left-0 right-0  w-full sticky bottom-0 pb-6 rounded-b-2xl bg-gradient-to-t from-black/25 to-transparent",
        isDark && "bg-gradient-to-t from-black/50 to-transparent"
      )}
    >
      <div
        className={cn(
          "flex items-center justify-center gap-2 px-12 py-4 w-fit mx-auto rounded-full border border-border shadow-lg bg-accent/50",
          isDark && "bg-secondary/50"
        )}
      >
        <ButtonAnswer
          render={({ onClick, isAvailable }) =>
            isAvailable ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant={isDark ? "outline" : "default"} onClick={onClick} className="md:size-[64px] size-[42px] rounded-full">
                    <Phone className="size-6" fill="currentColor" stroke="transparent" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("call-controls.answer")}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Fragment />
            )
          }
        />

        <ButtonDecline
          render={({ onClick, isAvailable }) =>
            isAvailable ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="destructive" className="md:size-[64px] size-[42px] rounded-full" onClick={onClick}>
                    <Phone className="rotate-[135deg] size-6" fill="currentColor" stroke="transparent" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("call-controls.decline")}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Fragment />
            )
          }
        />

        <ButtonResume
          render={({ onClick, isAvailable }) =>
            isAvailable ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("md:size-[64px] size-[42px] rounded-full", !isDark && "border-slate-300")}
                    onClick={onClick}
                  >
                    <Play className="size-6" fill="currentColor" stroke="transparent" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("call-controls.resume")}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Fragment />
            )
          }
        />

        <ButtonHold
          render={({ onClick, isAvailable }) =>
            isAvailable ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("md:size-[64px] size-[42px] rounded-full", !isDark && "border-slate-300")}
                    onClick={onClick}
                  >
                    <Pause className="size-6" fill="currentColor" stroke="transparent" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("call-controls.hold")}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Fragment />
            )
          }
        />

        <ButtonHangUp
          render={({ onClick, isAvailable }) =>
            isAvailable ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="destructive" className="md:size-[64px] size-[42px] rounded-full" onClick={onClick}>
                    <Phone className="rotate-[135deg] size-6" fill="currentColor" stroke="transparent" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("call-controls.hang-up")}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Fragment />
            )
          }
        />

        <Keypad
          render={({ onKeyPress, isAvailable }) => (
            <PopoverPhonepadWithTooltip onKeyPress={onKeyPress} isAvailable={isAvailable} tooltip={t("call-controls.keypad")} />
          )}
        />

        <CallInputs
          render={({ isMuted, mute, unmute }) => {
            const toggleMute = useCallback(() => {
              if (isMuted) {
                unmute();
              } else {
                mute();
              }
            }, [isMuted, mute, unmute]);

            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("md:size-[64px] size-[42px] rounded-full", !isDark && "border-slate-300")}
                    onClick={toggleMute}
                  >
                    {isMuted ? <MicOff className="size-6" /> : <Mic className="size-6" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isMuted ? t("call-controls.unmute-input") : t("call-controls.mute-input")}</p>
                </TooltipContent>
              </Tooltip>
            );
          }}
        />

        <CallOutputs
          render={({ volume, setVolume }) => {
            const [previousVolume, setPreviousVolume] = useState(volume);
            const isMuted = useMemo(() => volume === 0, [volume]);

            const mute = useCallback(() => {
              setPreviousVolume(volume);
              setVolume(0);
            }, [volume, setVolume]);

            const unmute = useCallback(() => {
              setVolume(previousVolume);
            }, [previousVolume, setVolume]);

            const toggleMute = useCallback(() => {
              if (isMuted) {
                unmute();
              } else {
                mute();
              }
            }, [isMuted, unmute, mute]);

            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("md:size-[64px] size-[42px] rounded-full", !isDark && "border-slate-300")}
                    onClick={toggleMute}
                  >
                    {isMuted ? <VolumeX className="size-6" /> : <Volume2 className="size-6" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isMuted ? t("call-controls.unmute-output") : t("call-controls.mute-output")}</p>
                </TooltipContent>
              </Tooltip>
            );
          }}
        />
      </div>
    </div>
  );
}
